import { createAsyncThunk } from '@reduxjs/toolkit';
import { fetchUserProfile } from '../../../user-profile/user-profile.thunks';
import { waitForUser } from '../../../../store/store';
import { GetShopCartParams, ShopCart } from '../interfaces/shop-cart.interface';
import {
  getOrderApplicationForAnalytics,
  isConsideredAsTunnel,
  NATIONAL_STORE,
  isHEInstructor
} from '../../../../common/utils/cart-util';
import { getUserDataForUserAnalytics, selectUser } from '../../../user-profile/user-profile.selectors';
import { StoreState } from '../../../../store/store.interface';
import { GENERIC_TRANSACTION_ERROR, SHARE_CART_ID } from '../../../../common/constants/api-constants';
import {
  getCartContext,
  getEstimatedTaxState,
  getGuestCartToken,
  getMergeGuestCartCallSucceeded,
  getPromoCodeState,
} from '../store/shop-cart.selectors';
import { isCustomerLoginSuccess, loadCustomer } from '../../../customer/customer';
import { isNationalFlow, isTunnelPurchaseFlow, retryApiCall } from '../../../../common/utils/util';
import { ActionStatus } from '../../../../common/constants/action-status';
import { Message, MessageType } from '../../../alert/global-message.interface';
import { PAYMENT_MESSAGE_ID, updateGlobalMessage } from '../../../alert/global-message';
import emit from '../../../../common/events';
import { getCartItemMagento, getTokenFromMagento } from './shop-cart-commons';
import { mergeShareCart } from './merge-share-cart.thunk';
import { mergeGuestCart } from './merge-guest-cart.thunk';
import { setHeInstructorInvalidProductsRemoved, unsetGuestCartToken } from '../store/shop-cart.store';
import {
  getAdditionalOrderAnalytics,
  getProductsWithAdditionalAnalytics,
} from '../../../../common/utils/analytics-util';
import { getCartAnalyticsOverrides } from '../shop-cart.analytics';

export const getShopCart = createAsyncThunk(
  'shopCart/getCart',
  async ({ store, promoCode }: GetShopCartParams, thunkAPI) => {
    try {
      thunkAPI.dispatch(fetchUserProfile());
      await waitForUser();
      await thunkAPI.dispatch(loadCustomer());
      const shopCart: ShopCart = await getCartMagento(thunkAPI, store, promoCode);

      // The states are being mapped here because they are needed inside the analytics event
      // As the shopCarState will be just available on the return of this thunk, the data
      // needs to be passed to the method directly simulating the state object
      const preState = getStateForAnalytics(thunkAPI, shopCart, store);
      emitAnalyticsCartLoad(shopCart, preState);

      return { ...shopCart, store };
    } catch (error) {
      const message: Message = {
        message: GENERIC_TRANSACTION_ERROR,
        type: MessageType.primary,
      };
      thunkAPI.dispatch(
        updateGlobalMessage({
          id: PAYMENT_MESSAGE_ID,
          ...message,
        })
      );
      return thunkAPI.rejectWithValue(error);
    }
  },
  {
    condition: ({ forceUpdate = false }: GetShopCartParams, { getState }) => {
      const state = getState() as StoreState;
      return forceUpdate || state.shopCartState.getShopCart.status === ActionStatus.idle;
    },
  }
);

function getStateForAnalytics(thunkAPI, shopCart, store) {
  const storeState = thunkAPI.getState() as StoreState;
  
  const stateForCartAnalytics = JSON.parse(JSON.stringify({ 
    shopCartState: storeState.shopCartState,
    estimatedTaxState: storeState.estimatedTaxState, 
    promoCodeDiscountState: storeState.promoCodeDiscountState, 
    userProfileState: storeState.userProfileState, 
    orderCompletionState : storeState.orderCompletionState 
  }));
  stateForCartAnalytics.shopCartState.getShopCart.data = { ...shopCart, store };
  stateForCartAnalytics.shopCartState.getShopCart.status = ActionStatus.succeeded;
  return stateForCartAnalytics;
}

async function getCartMagento(thunkAPI, store, promoCode) {
  await getTokenFromMagento(thunkAPI);
  const state = thunkAPI.getState() as StoreState;
  const urlParams = new URLSearchParams(location.search);
  const shareCartId = urlParams.get(SHARE_CART_ID);
  if (shareCartId) {
    const shopCartUrlContext = getCartContext(state, store, promoCode);
    shopCartUrlContext.shareCartId = shareCartId;
    await thunkAPI.dispatch(mergeShareCart(shopCartUrlContext));
  }
  
  if (isCustomerLoginSuccess(state) && getGuestCartToken(state)) {
    await thunkAPI.dispatch(mergeGuestCart(null as any));
  }
  let shopCart = await retryApiCall(
    () => getCartItemMagento(thunkAPI.getState() as StoreState),
    async () => {
      await thunkAPI.dispatch(unsetGuestCartToken());
      await getTokenFromMagento(thunkAPI);
    }
  );

  const updatedState = thunkAPI.getState() as StoreState;
  const user = selectUser(updatedState);
  const isHeInstructor = user && isHEInstructor(user);

  if (isHeInstructor && getMergeGuestCartCallSucceeded(updatedState)) {
    await thunkAPI.dispatch(setHeInstructorInvalidProductsRemoved(true));
  }

  const doesStoreAndShopCartStoreDiffer =
    (store && store !== shopCart.store) || (isNationalFlow() && shopCart.store !== NATIONAL_STORE);
  if (!isTunnelPurchaseFlow() && doesStoreAndShopCartStoreDiffer) {
    shopCart = { ...shopCart, products: [], productCount: 0 };
  }
  return shopCart;
}

function emitAnalyticsCartLoad(shopCart: ShopCart, state) {
  let analyticsName = 'load-cart';
  if (isHEInstructor(selectUser(state))) {
    analyticsName = 'instructor-sample-load-cart';
  }

  const details: any = {
    cart: getProductsWithAdditionalAnalytics({ products: shopCart.products }),
    isEdoms: !!shopCart.metaOfferId,
    flow: 'mcgraw',
    estimatedTax: getEstimatedTaxState(state),
    promoCode: getPromoCodeState(state)?.coupon,
    url: window.location.href,
    user: getUserDataForUserAnalytics(state, true),
    ...getAdditionalOrderAnalytics({
      products: shopCart.products,
    }),
    ...getCartAnalyticsOverrides(state)
  };

  if (isTunnelPurchaseFlow() || isConsideredAsTunnel(shopCart?.products)) {
    details.flow = 'tunnel-purchase';
    details.application = getOrderApplicationForAnalytics(shopCart?.products[0].productMetadata!);
  }

  emit({
    name: analyticsName,
    detail: details,
  });
}
